import { Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { isApiError } from '~/api/client'
import { useJoinById, useMe } from '~/api/hooks'
import { FaceStepBody, StillStep } from '~/biometrics/FaceStep'
import type { FaceDone } from '~/biometrics/types'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { FlowStepper } from '~/components/member/FlowStepper'
import { MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { StepSkeleton } from '~/components/member/StepSkeleton'

export const MEMBER_POLL_MS = 3000

/** After the check: poll `/api/member/me` until the card is no longer "needs action", then show the member page. */
export function WaitForCard({ onChanged }: { onChanged: () => void }) {
  const { t } = useT()
  const me = useMe()
  const { refetch } = me
  useEffect(() => {
    const id = setInterval(() => void refetch(), MEMBER_POLL_MS)
    return () => clearInterval(id)
  }, [refetch])
  useEffect(() => {
    if (me.isFetchedAfterMount && me.data && me.data.card !== 'needs_action') onChanged()
  }, [me.data, me.isFetchedAfterMount, onChanged])
  return (
    <Text role='status' aria-live='polite'>
      {t('wait.title')} {t('wait.text')}
    </Text>
  )
}

/**
 * `/me/face`: a member whose face check an administrator asked to repeat (R32) does it again while logged in, with
 * the member's own provider (`providerId` of `/api/member/me`). Afterwards the page polls `/api/member/me` until the
 * card changes (the API has no member-scope attempt status).
 */
export default function MemberFace() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const me = useMe()
  const join = useJoinById(me.data?.providerId)
  const [done, setDone] = useState(false)
  // `#still`: the provider's own check (FaceTec) just finished on its page; our still picture is the one step left.
  const still = useLocation().hash === '#still'

  if (me.isError && isApiError(me.error) && me.error.status === 401) return <Navigate to='/login' replace />
  const handle = (d: FaceDone) => {
    if (d.to === 'switch') return
    setDone(true)
  }
  return (
    <MemberLayout backTo='/me'>
      <FlowStepper step='face' />
      {(me.isPending || join.isPending) && !me.isError && <StepSkeleton />}
      {me.isError && <ErrorAlert>{friendly(me.error)}</ErrorAlert>}
      {join.isError && <ErrorAlert>{friendly(join.error)}</ErrorAlert>}
      {!done && me.data?.documentVerified === true && <Text>{t('join.document.alreadyVerified')}</Text>}
      {done ? (
        <WaitForCard onChanged={() => navigate('/me', { replace: true })} />
      ) : still ? (
        join.data && <StillStep join={join.data} onDone={() => setDone(true)} />
      ) : (
        join.data && (
          <FaceStepBody
            member
            join={join.data}
            tries={{ attempt: null, attemptsLeft: 1, canStart: true }}
            onDone={handle}
          />
        )
      )}
    </MemberLayout>
  )
}
